"use client";

import type { FormStep } from "./form-types";

import { cn } from "@homeinstead/ui";
import Stepper from "@homeinstead/ui/components/Stepper";
import { LeadFormStepperDisplay } from "./LeadFormStepperDisplay";
import { defaultLeadFormStyleClasses } from "./lead-form-styles";

type StepperProps = {
  currentStep: number;
  totalSteps: number;
  handleBack: () => void;
  showBackButton: boolean;
  showStepper: boolean;
};

/**
 * Decides which stepper variant to render at the form level:
 *
 * - Interstitial step with `stepperDisplay.variant === "progress"`:
 *   renders the centered animated progress bar (`LeadFormStepperDisplay`),
 *   keyed by `activeStep.id` so the fill animation restarts each time a new
 *   interstitial step becomes active.
 *
 * - Everything else: renders the classic `Stepper` (progress bar + step count
 *   + back button) constrained to the classic `stepperBase` width. Reserves
 *   its layout height via `invisible` when `showStepper` is false so the layout
 *   stays stable across steps.
 *
 * `className` carries only shared slot spacing (e.g. top padding); each variant
 * applies its own width/alignment so the progress bar centers instead of
 * inheriting the classic stepper's left-aligned fixed width.
 */
function LeadFormStepper({
  activeStep,
  stepperProps,
}: {
  activeStep: FormStep;
  stepperProps: StepperProps;
}) {
  const isProgressVariant =
    activeStep._type === "interstitialStep" &&
    activeStep.stepperDisplay?.variant === "progress";

  if (isProgressVariant) {
    return (
      <LeadFormStepperDisplay
        className="pt-8 pb-16 md:pt-16"
        currentStep={stepperProps.currentStep}
        key={activeStep.id}
        stepperDisplay={activeStep.stepperDisplay}
        totalSteps={stepperProps.totalSteps}
      />
    );
  }

  return (
    <Stepper
      {...stepperProps}
      aria-hidden={!stepperProps.showStepper}
      className={cn(
        defaultLeadFormStyleClasses.stepperBase,
        defaultLeadFormStyleClasses.stepperPaddingTop,
        {
          "pointer-events-none invisible select-none":
            !stepperProps.showStepper,
        },
      )}
      labelRowClassName={defaultLeadFormStyleClasses.stepperLabelRowSpacing}
    />
  );
}

export { LeadFormStepper };
